import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { useFlags } from "@/utils/launchDarkly";

import {
  ProceedToAccountNotFoundStepAtom,
  ProceedToClubSelectionStepAtom,
  ProceedToPasswordStep,
  ProceedToVerifyCodeStepAtom,
} from "../atoms/actions";
import { UserContactStatus } from "./__generated__/useCheckUserEmailStatusMutation.graphql";
import { useCheckUserEmailStatus } from "./useCheckUserEmailStatus";
import { useCheckUserPhoneStatus } from "./useCheckUserPhoneStatus";
import { useCreateEmailVerification } from "./useCreateEmailVerification";
import { useCreatePhoneVerification } from "./useCreatePhoneVerification";

// Tracking is handled inside action atoms; no direct tracking here

export const useCheckEmailorPhone = ({
  clubSlug,
  clubSupportsPublic,
}: {
  clubSlug?: string | null;
  clubSupportsPublic?: boolean | null;
}) => {
  const goToCodeVerification = useSetAtom(ProceedToVerifyCodeStepAtom);
  const goToEnterPassword = useSetAtom(ProceedToPasswordStep);
  const goToClubSelectionStep = useSetAtom(ProceedToClubSelectionStepAtom);
  const goToAccountNotFound = useSetAtom(ProceedToAccountNotFoundStepAtom);

  const [checkEmail, isCheckingEmail] = useCheckUserEmailStatus();
  const [checkPhone, isCheckingPhone] = useCheckUserPhoneStatus();
  const [createPhoneVerification, isCreatingPhoneVerification] =
    useCreatePhoneVerification();
  const [createEmailVerification, isCreatingEmailVerification] =
    useCreateEmailVerification();

  const isChecking =
    isCheckingEmail ||
    isCheckingPhone ||
    isCreatingPhoneVerification ||
    isCreatingEmailVerification;

  const { customerClublessSignup } = useFlags();

  // No direct tracking; actions will emit analytics

  const handleSuccessResponse = useCallback(
    (value: string, response: UserContactStatus, type: "Email" | "Phone") => {
      const email = type === "Email" ? value : null;
      const phone = type === "Phone" ? value : null;

      // Backend signals that no user exists yet, but a signup-eligible private-club
      // member is waiting on activation. Route to the AccountNotFound step so the
      // patron sees the "Activate your account" affordance — taken regardless of
      // the clubless-signup flag or whether a club slug is present.
      if (response === "ELIGIBLE_FOR_ACTIVATION") {
        goToAccountNotFound({ email, phone });
        return;
      }

      //IsNotTaken -> Send up verification check and proceed
      if (response === "NOT_TAKEN") {
        // Without a club, use clubless signup. At a private club, preserve the
        // account-not-found activation path.

        const shouldGoToAccountNotFound =
          !clubSlug || clubSupportsPublic !== true;

        const shouldUseClublessSignup = customerClublessSignup && !clubSlug;

        if (shouldGoToAccountNotFound) {
          if (shouldUseClublessSignup) {
            // If the account is not found and we don't have a club, go to the club selection step
            goToClubSelectionStep({ emailOrPhone: email ?? phone ?? "" });
          } else {
            // If the account is not found and we don't have a club, go to the account not found step
            goToAccountNotFound({
              email,
              phone,
            });
          }
        }
        //If the contact information is not taken, we need to create a verification
        //and then proceed to code entry step.
        else if (!!email) {
          createEmailVerification(email, () => {
            goToCodeVerification({ email, phone, type: "NewAccount" });
          });
        } else if (!!phone) {
          createPhoneVerification(phone, () => {
            goToCodeVerification({ email, phone, type: "NewAccount" });
          });
        }
      }
      //IsTakenEmail -> Proceed to password
      //IsTakenPhone -> Proceed to verification, and then log the user in
      if (response === "VERIFIED") {
        //If phone, proceed to code verification.
        if (!!phone) {
          createPhoneVerification(phone, () => {
            goToCodeVerification({ email, phone, type: "ExistingAccount" });
          });
        }

        //If Email, go to password screen
        if (!!email) {
          goToEnterPassword({ verifiedEmail: email });
        }
      }
      //IsTakenButNeedstoVerify -> Verify and then log the user in
      if (response === "UNVERIFIED") {
        if (!!email) {
          createEmailVerification(email, () => {
            goToCodeVerification({ email, phone, type: "ExistingAccount" });
          });
        } else if (!!phone) {
          createPhoneVerification(phone, () => {
            goToCodeVerification({ email, phone, type: "ExistingAccount" });
          });
        }
      }
    },
    [
      createEmailVerification,
      createPhoneVerification,
      goToCodeVerification,
      goToEnterPassword,
      clubSlug,
      clubSupportsPublic,
      goToAccountNotFound,
      customerClublessSignup,
      goToClubSelectionStep,
    ]
  );

  const checkEmailOrPhone = ({
    value,
    type,
  }: {
    value: string;
    type: "Email" | "Phone";
  }) => {
    if (type === "Email") {
      // Tracking moves into the subsequent action via atom
      checkEmail(value, handleSuccessResponse);
    } else {
      // Tracking moves into the subsequent action via atom
      checkPhone(value, handleSuccessResponse);
    }
  };

  return [checkEmailOrPhone, isChecking] as const;
};
